import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount, useChainId, useDisconnect } from 'wagmi';
import WalletConnect from '../components/WalletConnect';
import { ZNSAddressDisplay } from '../components/shared/ZNSAddressDisplay';
import { ZNSRegistrationModal } from '../components/shared/ZNSRegistrationModal';
import PriceFooter from '../components/shared/PriceFooter';
import { SUPPORTED_CHAINS } from '../utils/relayProtocol';

const HowToPage: React.FC = () => {
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { disconnect } = useDisconnect();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showZNSModal, setShowZNSModal] = useState(false);

  // Function to get current chain logo
  const getCurrentChainLogo = () => {
    const chain = Object.values(SUPPORTED_CHAINS).find(chain => chain.chainId === chainId);
    return chain?.logo || SUPPORTED_CHAINS.INK.logo;
  };

  const handleMobileNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'transparent',
      color: '#ffffff',
      fontFamily: 'Plus Jakarta Sans, sans-serif',
      paddingBottom: '60px'
    }}>
      <style>
        {`
          @font-face {
            font-family: 'Plus Jakarta Sans';
            src: url('/landing-assets/PlusJakartaSans-Regular.ttf') format('truetype');
            font-weight: 400;
            font-style: normal;
          }
          
          @font-face {
            font-family: 'Plus Jakarta Sans';
            src: url('/landing-assets/PlusJakartaSans-SemiBold.ttf') format('truetype');
            font-weight: 600;
            font-style: normal;
          }
          
          @font-face {
            font-family: 'Plus Jakarta Sans';
            src: url('/landing-assets/PlusJakartaSans-Bold.ttf') format('truetype');
            font-weight: 700;
            font-style: normal;
          }

          .howto-container {
            max-width: 1600px;
            margin: 0 auto;
            padding: 0 20px;
          }

          .howto-header {
            padding: 20px 0;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          }

          .howto-header-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
          }

          .howto-logo {
            height: 40px;
            cursor: pointer;
          }

          .howto-nav-links {
            display: flex;
            gap: 4px;
            align-items: center;
            flex-shrink: 1;
          }

          .howto-nav-link {
            padding: 10px 14px;
            background: transparent;
            border: none;
            border-radius: 8px;
            color: #ffffff;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
            font-family: 'Plus Jakarta Sans', sans-serif;
            text-decoration: none;
            white-space: nowrap;
          }

          .howto-nav-link:hover {
            background: rgba(88, 28, 135, 0.3);
          }

          .howto-right-section {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-shrink: 0;
          }

          .howto-wallet-badge {
            padding: 10px 16px;
            background: rgba(88, 28, 135, 0.3);
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.3s ease;
            border: none;
            color: #ffffff;
          }

          .howto-wallet-badge:hover {
            background: rgba(88, 28, 135, 0.5);
          }

          .howto-wallet-badge * {
            color: #ffffff !important;
          }

          .howto-chain-logo {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            object-fit: cover;
          }

          .howto-hamburger {
            display: none;
            background: transparent;
            border: none;
            color: #ffffff;
            font-size: 24px;
            cursor: pointer;
            padding: 8px;
            transition: all 0.3s ease;
          }

          .howto-hamburger:hover {
            color: #a855f7;
          }

          .howto-mobile-menu-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.7);
            backdrop-filter: blur(4px);
            z-index: 999;
            display: none;
          }

          .howto-mobile-menu-overlay.open {
            display: block;
          }

          .howto-mobile-menu {
            position: fixed;
            top: 0;
            right: 0;
            bottom: 0;
            width: 280px;
            max-width: 85vw;
            background: rgba(18, 18, 26, 0.98);
            backdrop-filter: blur(20px);
            border-left: 1px solid rgba(168, 85, 247, 0.2);
            z-index: 1000;
            transform: translateX(100%);
            transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            overflow-y: auto;
            display: flex;
            flex-direction: column;
          }

          .howto-mobile-menu.open {
            transform: translateX(0);
          }

          .howto-mobile-menu-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 16px 20px;
            border-bottom: 1px solid rgba(168, 85, 247, 0.2);
          }

          .howto-mobile-menu-logo {
            height: 24px;
          }

          .howto-mobile-close {
            background: transparent;
            border: none;
            color: #ffffff;
            font-size: 24px;
            cursor: pointer;
            padding: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
          }

          .howto-mobile-close:hover {
            color: #a855f7;
          }

          .howto-mobile-nav-links {
            flex: 1;
            padding: 8px 0;
          }

          .howto-mobile-nav-link {
            width: 100%;
            padding: 16px 20px;
            background: transparent;
            border: none;
            color: #ffffff;
            font-size: 16px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
            text-align: left;
            font-family: 'Plus Jakarta Sans', sans-serif;
            display: block;
          }

          .howto-mobile-nav-link:hover {
            background: rgba(88, 28, 135, 0.3);
          }

          .howto-mobile-wallet-section {
            padding: 20px 20px 100px 20px;
            border-top: 1px solid rgba(168, 85, 247, 0.2);
            margin-top: auto;
          }

          .howto-content {
            max-width: 1600px;
            margin: 0 auto;
            padding: 60px 40px;
          }

          .howto-page-logo {
            display: block;
            margin: 0 auto 40px auto;
            max-width: 500px;
            width: 100%;
            height: auto;
          }

          .howto-title {
            font-size: 48px;
            font-weight: 700;
            color: #ffffff;
            margin-bottom: 16px;
            font-family: 'Plus Jakarta Sans', sans-serif;
            text-align: center;
          }

          .howto-subtitle {
            font-size: 18px;
            color: rgba(255, 255, 255, 0.7);
            margin-bottom: 60px;
            font-family: 'Plus Jakarta Sans', sans-serif;
            text-align: center;
          }

          .howto-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 32px;
            margin-bottom: 40px;
          }

          .howto-card {
            background: rgba(0, 0, 0, 0.3);
            border-radius: 16px;
            overflow: hidden;
            transition: all 0.3s ease;
            cursor: pointer;
            text-decoration: none;
            color: inherit;
            display: flex;
            flex-direction: column;
            border: 1px solid rgba(255, 255, 255, 0.1);
            aspect-ratio: 2 / 1;
          }

          .howto-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 12px 40px rgba(168, 85, 247, 0.3);
            border-color: rgba(168, 85, 247, 0.5);
          }

          .howto-thumbnail-container {
            width: 100%;
            flex: 1;
            overflow: hidden;
            background: rgba(0, 0, 0, 0.2);
            min-height: 0;
          }

          .howto-thumbnail {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .howto-card-content {
            padding: 12px 16px 16px 16px;
            display: flex;
            flex-direction: column;
            background: rgba(0, 0, 0, 0.4);
            min-height: 90px;
            max-height: 120px;
          }

          .howto-card-title {
            font-size: 14px;
            font-weight: 700;
            color: #ffffff;
            margin: 0 0 6px 0;
            font-family: 'Plus Jakarta Sans', sans-serif;
            line-height: 1.3;
            max-height: 2.6em;
            overflow: hidden;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
          }

          .howto-card-description {
            font-size: 12px;
            color: rgba(255, 255, 255, 0.7);
            line-height: 1.4;
            margin: 0;
            font-family: 'Plus Jakarta Sans', sans-serif;
            overflow: hidden;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
          }

          .howto-footer {
            padding: 80px 0 40px 0;
            text-align: center;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
          }

          .howto-all-sections {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 30px;
            max-width: 1200px;
            margin: 0 auto;
          }

          .howto-footer-text {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 400;
            font-size: 1.2rem;
            margin-bottom: 40px;
            color: rgba(255, 255, 255, 0.7);
          }

          .howto-footer-logos {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 15px;
            flex-wrap: nowrap;
            max-width: 100%;
            overflow: hidden;
          }

          .howto-footer-logos img {
            height: 45px;
            max-width: 140px;
            opacity: 0.7;
            transition: opacity 0.3s ease;
            object-fit: contain;
          }

          .howto-footer-logos img:hover {
            opacity: 1;
          }

          .howto-plus-icon {
            font-size: 24px;
            color: rgba(255, 255, 255, 0.6);
            margin: 0 10px;
          }

          @media (max-width: 1400px) {
            .howto-content {
              max-width: 1200px;
            }
          }

          @media (max-width: 1024px) {
            .howto-grid {
              grid-template-columns: repeat(2, 1fr);
              gap: 28px;
            }

            .howto-nav-links {
              display: none;
            }

            .howto-hamburger {
              display: block;
            }
          }

          @media (max-width: 768px) {
            .howto-content {
              padding: 40px 20px;
              max-width: 100%;
            }

            .howto-page-logo {
              max-width: 300px;
              margin-bottom: 30px;
            }

            .howto-title {
              font-size: 32px;
            }

            .howto-subtitle {
              font-size: 16px;
              margin-bottom: 40px;
            }

            .howto-grid {
              grid-template-columns: 1fr;
              gap: 24px;
            }

            .howto-card-content {
              min-height: 80px;
              max-height: 100px;
            }

            .howto-card-title {
              font-size: 13px;
            }

            .howto-card-description {
              font-size: 11px;
              -webkit-line-clamp: 2;
            }

            .howto-all-sections {
              grid-template-columns: 1fr;
              gap: 40px;
            }

            .howto-footer-text {
              font-size: 1rem;
              margin-bottom: 30px;
            }

            .howto-footer-logos {
              gap: 10px;
            }

            .howto-footer-logos img {
              height: 30px;
              max-width: 90px;
            }

            .howto-plus-icon {
              font-size: 18px;
              margin: 0 5px;
            }
          }

          @media (max-width: 480px) {
            .howto-footer-text {
              font-size: 0.9rem;
              margin-bottom: 25px;
            }

            .howto-footer-logos {
              gap: 8px;
            }

            .howto-footer-logos img {
              height: 24px;
              max-width: 70px;
            }

            .howto-plus-icon {
              font-size: 16px;
              margin: 0 3px;
            }
          }
        `}
      </style>

      {/* Header */}
      <header className="howto-header">
        <div className="howto-container">
          <div className="howto-header-content">
            <img
              src="/landing-assets/mavrk_word_logo.png"
              alt="Mavrk"
              className="howto-logo"
              onClick={() => navigate('/')}
            />
            <div className="howto-nav-links">
              <button onClick={() => navigate('/create')} className="howto-nav-link">Create Token</button>
              <button onClick={() => navigate('/explore')} className="howto-nav-link">Explore</button>
              <button onClick={() => navigate('/bridge')} className="howto-nav-link">Bridge</button>
              <button onClick={() => navigate('/swap')} className="howto-nav-link">Swap</button>
              <button onClick={() => navigate('/locks')} className="howto-nav-link">Locks & TVL</button>
              <button onClick={() => navigate('/tydro')} className="howto-nav-link">Tydro</button>
              <button onClick={() => navigate('/pay')} className="howto-nav-link">Pay</button>
              <button onClick={() => setShowZNSModal(true)} className="howto-nav-link">ZNS</button>
              <button onClick={() => navigate('/profile')} className="howto-nav-link">Profile</button>
              <button onClick={() => window.open('/docs', '_blank')} className="howto-nav-link">Docs</button>
            </div>
            <div className="howto-right-section">
              {isConnected && address ? (
                <>
                  <button
                    className="howto-wallet-badge"
                    onClick={() => disconnect()}
                    title="Click to disconnect wallet"
                  >
                    <ZNSAddressDisplay address={address} truncateLength={4} showIcon={true} />
                  </button>
                  <img
                    src={getCurrentChainLogo()}
                    alt="Current Chain"
                    className="howto-chain-logo"
                  />
                </>
              ) : (
                <WalletConnect />
              )}
            </div>
            <button
              className="howto-hamburger"
              onClick={() => setMobileMenuOpen(true)}
            >
              ☰
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`howto-mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu */}
      <div className={`howto-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="howto-mobile-menu-header">
          <img
            src="/landing-assets/mavrk_word_logo.png"
            alt="Mavrk"
            className="howto-mobile-menu-logo"
          />
          <button
            className="howto-mobile-close"
            onClick={() => setMobileMenuOpen(false)}
          >
            ×
          </button>
        </div>

        <div className="howto-mobile-nav-links">
          <button
            onClick={() => { navigate('/create'); handleMobileNavClick(); }}
            className="howto-mobile-nav-link"
          >
            Create Token
          </button>
          <button
            onClick={() => { navigate('/explore'); handleMobileNavClick(); }}
            className="howto-mobile-nav-link"
          >
            Explore
          </button>
          <button
            onClick={() => { navigate('/bridge'); handleMobileNavClick(); }}
            className="howto-mobile-nav-link"
          >
            Bridge
          </button>
          <button
            onClick={() => { navigate('/swap'); handleMobileNavClick(); }}
            className="howto-mobile-nav-link"
          >
            Swap
          </button>
          <button
            onClick={() => { navigate('/locks'); handleMobileNavClick(); }}
            className="howto-mobile-nav-link"
          >
            Locks & TVL
          </button>
          <button
            onClick={() => { navigate('/tydro'); handleMobileNavClick(); }}
            className="howto-mobile-nav-link"
          >
            Tydro
          </button>
          <button
            onClick={() => { navigate('/pay'); handleMobileNavClick(); }}
            className="howto-mobile-nav-link"
          >
            Pay
          </button>
          <button
            onClick={() => { setShowZNSModal(true); handleMobileNavClick(); }}
            className="howto-mobile-nav-link"
          >
            ZNS
          </button>
          <button
            onClick={() => { navigate('/profile'); handleMobileNavClick(); }}
            className="howto-mobile-nav-link"
          >
            Profile
          </button>
          <button
            onClick={() => { window.open('/docs', '_blank'); handleMobileNavClick(); }}
            className="howto-mobile-nav-link"
          >
            Docs
          </button>
        </div>

        <div className="howto-mobile-wallet-section">
          {isConnected && address ? (
            <button
              className="howto-wallet-badge"
              onClick={() => { disconnect(); handleMobileNavClick(); }}
              title="Click to disconnect wallet"
              style={{ width: '100%', marginBottom: '12px' }}
            >
              <ZNSAddressDisplay address={address} truncateLength={4} showIcon={true} />
            </button>
          ) : (
            <WalletConnect />
          )}
        </div>
      </div>

      {/* Content */}
      <div className="howto-content">
        <img
          src="/How%20To%20Page/mavrkdocs.png"
          alt="Mavrk Docs"
          className="howto-page-logo"
        />
        <h1 className="howto-title">How-To Guides</h1>
        <p className="howto-subtitle">
          Comprehensive guides to help you get started with Mavrk
        </p>

        <div className="howto-grid">
          {/* ROW 1 */}

          {/* Create a Token Guide */}
          <a
            href="/How%20To%20Page/how-to-create-a-token.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="howto-card"
          >
            <div className="howto-thumbnail-container">
              <img
                src="/How%20To%20Page/how-to-create-a-token-thumbnail.png"
                alt="Create a Token"
                className="howto-thumbnail"
              />
            </div>
            <div className="howto-card-content">
              <h3 className="howto-card-title">Create a Token</h3>
              <p className="howto-card-description">
                Learn how to create and deploy your own token on Mavrk with our step-by-step guide.
              </p>
            </div>
          </a>

          {/* Bridge to Ink Guide */}
          <a
            href="/How%20To%20Page/how-to-bridge-to-ink.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="howto-card"
          >
            <div className="howto-thumbnail-container">
              <img
                src="/How%20To%20Page/how-to-bridge-to-ink-thumbnail.png"
                alt="Bridge to Ink"
                className="howto-thumbnail"
              />
            </div>
            <div className="howto-card-content">
              <h3 className="howto-card-title">Bridge to Ink</h3>
              <p className="howto-card-description">
                Learn how to bridge assets from over 75 chains to Ink quickly and efficiently, powered by Relay Protocol.
              </p>
            </div>
          </a>

          {/* Cross-Chain Trading Guide */}
          <a
            href="/How%20To%20Page/how-to-trade-cross-chain.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="howto-card"
          >
            <div className="howto-thumbnail-container">
              <img
                src="/How%20To%20Page/how-to-trade-cross-chain-thumbnail.png"
                alt="Cross-Chain Trading"
                className="howto-thumbnail"
              />
            </div>
            <div className="howto-card-content">
              <h3 className="howto-card-title">Cross-Chain Trading</h3>
              <p className="howto-card-description">
                Discover how to trade tokens across multiple chains seamlessly using Relay Protocol.
              </p>
            </div>
          </a>

          {/* Collect Creator Earnings Guide */}
          <a
            href="/How%20To%20Page/how-to-collect-creator-earnings.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="howto-card"
          >
            <div className="howto-thumbnail-container">
              <img
                src="/How%20To%20Page/how-to-collect-creator-earnings-thumbnail.png"
                alt="Collect Creator Earnings"
                className="howto-thumbnail"
              />
            </div>
            <div className="howto-card-content">
              <h3 className="howto-card-title">Collect Creator Earnings</h3>
              <p className="howto-card-description">
                Learn how to collect your 25% creator earnings from token trading fees on Mavrk.
              </p>
            </div>
          </a>

          {/* ROW 2 */}

          {/* Tydro Leveraged Trading Guide */}
          <a
            href="/How%20To%20Page/tydro-leveraged-trading.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="howto-card"
          >
            <div className="howto-thumbnail-container">
              <img
                src="/How%20To%20Page/tydro-leveraged-trading-thumnail.png"
                alt="Tydro Leveraged Trading"
                className="howto-thumbnail"
              />
            </div>
            <div className="howto-card-content">
              <h3 className="howto-card-title">Tydro Leveraged Trading</h3>
              <p className="howto-card-description">
                Master leveraged trading with Tydro Protocol - supply, borrow, and trade with leverage.
              </p>
            </div>
          </a>

          {/* Register a .ink Domain Name Guide */}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); alert('PDF coming soon!'); }}
            className="howto-card"
          >
            <div className="howto-thumbnail-container">
              <img
                src="/How%20To%20Page/how-to-register-a-ink-domain-thumbnail.png"
                alt="Register a .ink Domain Name"
                className="howto-thumbnail"
              />
            </div>
            <div className="howto-card-content">
              <h3 className="howto-card-title">Register a .ink Domain Name with ZNS</h3>
              <p className="howto-card-description">
                Learn how to register your very own .ink domain name with ZNS connect and secure your web3 identity!
              </p>
            </div>
          </a>

          {/* Lock Tokens Guide */}
          <a
            href="/How%20To%20Page/how-to-lock-tokens.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="howto-card"
          >
            <div className="howto-thumbnail-container">
              <img
                src="/How%20To%20Page/how-to-lock-tokens-thumbnail.png"
                alt="Lock Tokens"
                className="howto-thumbnail"
              />
            </div>
            <div className="howto-card-content">
              <h3 className="howto-card-title">Lock Tokens</h3>
              <p className="howto-card-description">
                Learn how to lock your tokens in the Mavrk Token Locker. Lock tokens for however long you want!
              </p>
            </div>
          </a>

          {/* Create a Linear Vesting Lock Guide */}
          <a
            href="/How%20To%20Page/how-to-create-a-vesting-lock.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="howto-card"
          >
            <div className="howto-thumbnail-container">
              <img
                src="/How%20To%20Page/how-to-create-a-vesting-lock-thumbnail.png"
                alt="Create a Linear Vesting Lock"
                className="howto-thumbnail"
              />
            </div>
            <div className="howto-card-content">
              <h3 className="howto-card-title">Create a Linear Vesting Lock</h3>
              <p className="howto-card-description">
                Learn how to create a linear vesting schedule using our Mavrk Linear Vesting contract.
              </p>
            </div>
          </a>

          {/* Mavrk Token Deployment Widget Guide */}
          <a
            href="/How%20To%20Page/mavrk-token-deployment-widget-pdf.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="howto-card"
            onClick={(e) => {
              // Open PDF in completely new window context to avoid Supabase initialization
              e.preventDefault();
              const pdfUrl = window.location.origin + '/How%20To%20Page/mavrk-token-deployment-widget-pdf.pdf';
              window.open(pdfUrl, '_blank', 'noopener,noreferrer');
            }}
          >
            <div className="howto-thumbnail-container">
              <img
                src="/How%20To%20Page/mavrk-token-deployment-widget-thumbnail.png"
                alt="Mavrk Token Deployment Widget"
                className="howto-thumbnail"
                onError={(e) => {
                  console.error('Failed to load thumbnail image');
                  const img = e.target as HTMLImageElement;
                  // Try alternative path without encoding
                  img.src = '/How To Page/mavrk-token-deployment-widget-thumbnail.png';
                }}
              />
            </div>
            <div className="howto-card-content">
              <h3 className="howto-card-title">Mavrk Token Deployment Widget</h3>
              <p className="howto-card-description">
                Mavrk Token Deployments as a Service. Add Mavrk token deployments to your web app.
              </p>
            </div>
          </a>
        </div>
      </div>

      {/* Footer - Built On, Powered By, Unleashed By */}
      <footer className="howto-footer">
        <div className="howto-all-sections">
          {/* Built On Section */}
          <div>
            <p className="howto-footer-text">built on</p>
            <div className="howto-footer-logos">
              <img src="/landing-assets/OP_superchain.png" alt="OP Superchain" />
              <span className="howto-plus-icon">+</span>
              <img src="/landing-assets/Ink.png" alt="Ink" />
              <span className="howto-plus-icon">+</span>
              <img src="/landing-assets/ethereum.png" alt="Ethereum" />
            </div>
          </div>

          {/* Powered By Section */}
          <div>
            <p className="howto-footer-text">powered by</p>
            <div className="howto-footer-logos">
              <img src="/landing-assets/poweredbyRelay.png" alt="Powered by Relay" />
              <img src="/landing-assets/poweredbyZNS.png" alt="Powered by ZNS" />
            </div>
          </div>

          {/* Unleashed By Section */}
          <div>
            <p className="howto-footer-text">unleashed by</p>
            <div className="howto-footer-logos">
              <img src="/landing-assets/unleashedbyKraken.png" alt="Unleashed by Kraken" />
              <img src="/landing-assets/unleashedbyUniswap.png" alt="Unleashed by Uniswap" />
            </div>
          </div>
        </div>
      </footer>

      {/* ZNS Registration Modal */}
      <ZNSRegistrationModal
        isVisible={showZNSModal}
        onClose={() => setShowZNSModal(false)}
      />

      {/* Price Footer with ETH, kBTC prices and search */}
      <PriceFooter />
    </div>
  );
};

export default HowToPage;
